import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { checkpointToShapes } from '@/lib/checkpoint/checkpoint-to-shapes'
import { shapeSnapshotNestingDepth } from '@/lib/groups/clipboard-group-policy'
import { normalizeTemplateClikArtFill } from './helpers/normalize-template-clikart-fill'
import { wrapTemplateElement } from './helpers/wrap-template-element'
import type { BuildTemplatePlacementInput } from './types/build-template-placement-input'
import type { TemplatePlacement } from './types/template-placement'

/**
 * A template layer's elements as shapes for the spread, the way legacy places one: every element
 * lands at the geometry the template stores (template and spread share one spine-centered frame,
 * so there is no offset, scale or fit), in the template's own stacking order.
 *
 * The backend has already made each element a new object for this spread: a new id, a fractional
 * index above the spread's top element, and a uid above the spread's highest. They are kept as
 * they are, and so are the template's lock flags, because legacy copies those unchanged and the
 * template's text chains name the uids.
 *
 * Shapes load through `checkpointToShapes`, the same reader a reloaded spread uses, so what is
 * placed is what a reload would show and what recomposes.
 */
export function buildTemplatePlacement({
  elements,
  projection,
}: BuildTemplatePlacementInput): TemplatePlacement {
  const { shapes: loaded, diagnostics } = checkpointToShapes(
    {
      nsDocument: {
        pasteboardElements: { userLayer: { elements: elements.map(wrapTemplateElement) } },
      },
    },
    projection
  )
  const shapes = loaded.map(normalizeTemplateClikArtFill)

  // `loaded` is in stacking order, a group before its children. A group must be created after
  // every descendant it needs, so create deepest-nested first (stable otherwise).
  const byId = new Map(shapes.map((shape) => [shape.id, shape]))
  const creationOrder = shapes
    .map((shape, index) => ({ shape, index }))
    .sort(
      (a, b) =>
        shapeSnapshotNestingDepth(b.shape, byId) - shapeSnapshotNestingDepth(a.shape, byId) ||
        a.index - b.index
    )
    .map(({ shape }) => shape)

  return {
    shapes: creationOrder,
    stackingOrder: shapes.map((shape: ShapeData) => shape.id),
    maxUid: shapes.reduce((max, shape) => Math.max(max, shape.uid ?? 0), 0),
    skippedCount: diagnostics.skippedNoGeometry,
  }
}
